import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Loader2, ShieldCheck, Download, XCircle, Receipt } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Section } from "@/components/blocks";
import { useCart } from "@/context/CartContext";
import api from "@/lib/api";
import { trackPurchase } from "@/lib/analytics";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clear } = useCart();
  const [data, setData] = useState(null);
  const [state, setState] = useState("checking");
  const tries = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let stop = false;
    const poll = async () => {
      try {
        const r = await api.get(`/stripe/status/${sessionId}`);
        if (stop) return;
        if (r.data.payment_status === "paid") {
          setData(r.data);
          setState("paid");
          trackPurchase(sessionId, r.data.items, r.data.order?.amount);
          clear();
          return;
        }
        if (["expired", "failed"].includes(r.data.payment_status)) { setState("failed"); return; }
      } catch { if (stop) return; }
      if (++tries.current >= 8) { setState("timeout"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { stop = true; };
  }, [sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Section>
      <Card className="max-w-xl mx-auto p-10 text-center border-slate-200" data-testid="payment-success-page">
        {state === "checking" && (
          <>
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-teal-600" />
            <h1 className="font-display text-2xl font-bold text-slate-900 mt-5">Confirming your payment…</h1>
            <p className="text-slate-600 mt-2 text-sm">This only takes a moment.</p>
          </>
        )}
        {state === "paid" && data && (
          <>
            <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4"><ShieldCheck className="w-7 h-7 text-emerald-600" /></div>
            <h1 className="font-display text-3xl font-bold text-slate-900" data-testid="payment-success-title">Payment received</h1>
            <p className="text-slate-600 mt-2">Thank you! A receipt{data.order?.buyer_email ? ` has been sent to ${data.order.buyer_email}` : " is on its way"}.</p>
            <ul className="mt-6 text-left divide-y divide-slate-100 border border-slate-200 rounded-xl overflow-hidden" data-testid="payment-items">
              {data.items.map((it, i) => (
                <li key={i} className="flex justify-between px-4 py-3 text-sm"><span className="text-slate-800">{it.item_name}</span><span className="font-semibold text-slate-900">${it.amount}</span></li>
              ))}
              <li className="flex justify-between px-4 py-3 text-sm bg-slate-50 font-semibold"><span>Total</span><span data-testid="payment-total">${data.order.amount} {data.order.currency}</span></li>
            </ul>
            {data.downloads?.length > 0 && (
              <div className="mt-6 space-y-2 text-left">
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Your downloads (valid 30 days)</p>
                {data.downloads.map((d) => (
                  <Button key={d.item_id} asChild className="w-full justify-start bg-emerald-600 hover:bg-emerald-700 rounded-full" data-testid={`payment-download-${d.item_id}`}>
                    <a href={d.url} target="_blank" rel="noreferrer"><Download className="w-4 h-4" /> {d.title}</a>
                  </Button>
                ))}
              </div>
            )}
            <p className="text-xs text-slate-500 mt-6">Workshop seats, recordings and re-downloads live in <Link to="/account" className="text-teal-700 underline">My Account</Link> when you sign in with the same email.</p>
            <div className="mt-6 flex justify-center gap-3">
              <Button asChild className="rounded-full bg-teal-600 hover:bg-teal-700"><Link to="/account"><Receipt className="w-4 h-4" /> My Account</Link></Button>
              <Button asChild variant="outline" className="rounded-full"><Link to="/resources">Keep browsing</Link></Button>
            </div>
          </>
        )}
        {(state === "failed" || state === "error" || state === "timeout") && (
          <>
            <XCircle className="w-10 h-10 text-amber-500 mx-auto mb-3" />
            <h1 className="font-display text-2xl font-bold text-slate-900">{state === "timeout" ? "Still processing" : "We couldn't confirm that payment"}</h1>
            <p className="text-slate-600 mt-2 text-sm">{state === "timeout" ? "Your payment is taking longer than usual. Check your email for a receipt, or refresh this page in a minute." : "If money left your account, email us and we'll sort it out straight away."}</p>
            <div className="mt-6 flex justify-center gap-3">
              <Button onClick={() => window.location.reload()} className="rounded-full bg-slate-900" data-testid="payment-retry">Check again</Button>
              <Button asChild variant="outline" className="rounded-full"><Link to="/contact">Contact us</Link></Button>
            </div>
          </>
        )}
      </Card>
    </Section>
  );
}

export function PaymentCancel() {
  return (
    <Section>
      <Card className="max-w-lg mx-auto p-10 text-center border-slate-200" data-testid="payment-cancel-page">
        <XCircle className="w-10 h-10 text-slate-400 mx-auto mb-3" />
        <h1 className="font-display text-2xl font-bold text-slate-900">Checkout cancelled</h1>
        <p className="text-slate-600 mt-2 text-sm">No payment was taken. Your basket is still saved if you'd like to try again.</p>
        <Button asChild className="mt-6 rounded-full bg-teal-600 hover:bg-teal-700"><Link to="/resources">Back to resources</Link></Button>
      </Card>
    </Section>
  );
}
